<template>
  <div>
    <TopBar currentTitle="Manage" />
    <div class="page-content">
      <h1 class="page-title">Manage</h1>
      <p class="page-subtitle mb-4">Admin tools and data management</p>

      <!-- Admin Role Section -->
      <FeedsCard title="Make user an admin">
        <b-field>
          <b-input
            v-model="adminEmail"
            type="email"
            icon="account"
            placeholder="Admin Email"
          />
        </b-field>
        <b-field grouped>
          <b-button class="is-flex-grow-1 mr-1" type="is-danger" @click="updateAdmin(false)">Remove Admin</b-button>
          <b-button class="is-flex-grow-5 ml-1" type="is-primary" @click="updateAdmin(true)">Make Admin</b-button>
        </b-field>
      </FeedsCard>

      <!-- Sync Section -->
      <FeedsCard title="Data Sync" class="mt-5">
        <b-field label="Sync all de-normalised recipe data on mealplans">
          <b-button expanded type="is-primary" @click="syncRecipeData">Sync Recipe Data</b-button>
        </b-field>
      </FeedsCard>

      <!-- Backfill Tool Section -->
      <FeedsCard title="Backfill Property" class="mt-5">
        <p class="form-hint mb-4">Add a property with a default value to all documents in a collection. Only updates documents where the property is missing.</p>

        <div class="backfill-form">
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Collection</label>
              <b-input v-model="backfill.collection" placeholder="e.g. ingredients" />
            </div>
            <div class="form-group">
              <label class="form-label">Property Name</label>
              <b-input v-model="backfill.property" placeholder="e.g. item_type" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Value Type</label>
              <b-select v-model="backfill.valueType" expanded>
                <option value="string">String</option>
                <option value="number">Number</option>
                <option value="boolean">Boolean</option>
                <option value="null">Null</option>
              </b-select>
            </div>
            <div class="form-group">
              <label class="form-label">Default Value</label>
              <b-select v-if="backfill.valueType === 'boolean'" v-model="backfill.rawValue" expanded>
                <option value="true">true</option>
                <option value="false">false</option>
              </b-select>
              <b-input v-else-if="backfill.valueType === 'null'" value="null" disabled />
              <b-input
                v-else
                v-model="backfill.rawValue"
                :type="backfill.valueType === 'number' ? 'number' : 'text'"
                :placeholder="backfill.valueType === 'number' ? '0' : 'value'"
              />
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">
              <div class="inline-toggle">
                <span>Only where missing</span>
                <div
                  class="toggle"
                  :class="{ active: backfill.onlyMissing }"
                  @click="backfill.onlyMissing = !backfill.onlyMissing">
                </div>
              </div>
            </label>
          </div>

          <!-- Dry Run Results -->
          <div v-if="backfill.dryRunResult !== null" class="backfill-result mt-4">
            <div class="result-box">
              <p><strong>{{ backfill.dryRunResult.total }}</strong> documents in <code>{{ backfill.collection }}</code></p>
              <p><strong>{{ backfill.dryRunResult.affected }}</strong> would be updated ({{ backfill.onlyMissing ? 'missing property' : 'all documents' }})</p>
              <p>Property: <code>{{ backfill.property }}</code> = <code>{{ backfill.dryRunResult.displayValue }}</code></p>
            </div>
          </div>

          <!-- Progress -->
          <div v-if="backfill.isRunning" class="mt-4">
            <b-progress :value="backfill.progress" :max="backfill.progressMax" type="is-primary" show-value>
              {{ backfill.progress }} / {{ backfill.progressMax }}
            </b-progress>
          </div>

          <!-- Completion -->
          <div v-if="backfill.completedCount !== null && !backfill.isRunning" class="backfill-result mt-4">
            <div class="result-box success">
              <p>Updated <strong>{{ backfill.completedCount }}</strong> documents.</p>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="backfill-actions mt-4">
            <b-button
              type="is-primary"
              outlined
              :disabled="!isBackfillValid || backfill.isRunning"
              @click="dryRun">
              Dry Run
            </b-button>
            <b-button
              type="is-primary"
              :disabled="!isBackfillValid || backfill.isRunning || backfill.dryRunResult === null"
              @click="confirmBackfill">
              Run Backfill
            </b-button>
            <b-button
              v-if="backfill.rollbackLog.length > 0 && !backfill.isRunning"
              type="is-warning"
              @click="confirmRollback">
              Rollback Last Operation ({{ backfill.rollbackLog.length }} docs)
            </b-button>
          </div>
        </div>
      </FeedsCard>

      <!-- Saved Recipes Section -->
      <FeedsCard title="User Setup" class="mt-5">
        <b-field label="Add default saved recipes collection for users missing one">
          <b-button expanded type="is-primary" @click="createCollectionForUser">Add default saved recipes</b-button>
        </b-field>
      </FeedsCard>
    </div>
  </div>
</template>

<script>
import firebase from 'firebase/app'
import db from "@/firebase/db"
import functions from "@/firebase/functions"
import FeedsCard from "@/components/common/FeedsCard"
import TopBar from "@/components/layout/TopBar"

export default {
  name: 'ManageView',
  components: {
    FeedsCard,
    TopBar
  },
  data() {
    return {
      adminEmail: '',
      backfill: {
        collection: '',
        property: '',
        valueType: 'string',
        rawValue: '',
        onlyMissing: true,
        dryRunResult: null,
        isRunning: false,
        progress: 0,
        progressMax: 0,
        completedCount: null,
        rollbackLog: []
      }
    };
  },
  computed: {
    isBackfillValid() {
      return this.backfill.collection.trim() !== '' && this.backfill.property.trim() !== ''
    },
    parsedBackfillValue() {
      switch (this.backfill.valueType) {
        case 'null': return null
        case 'boolean': return this.backfill.rawValue === 'true'
        case 'number': return Number(this.backfill.rawValue) || 0
        default: return this.backfill.rawValue
      }
    }
  },
  methods: {
    updateAdmin(admin) {
      const updateAdminRole = functions.httpsCallable("updateAdminRole");
      updateAdminRole({ email: this.adminEmail, admin: admin })
        .then(result => {
          this.adminEmail = '';
          this.$buefy.toast.open({ type: 'is-success', message: `${result.data.message}` });
        }).catch(error => {
          this.$buefy.toast.open({ type: 'is-danger', message: `${error}` });
        });
    },
    syncRecipeData() {
      this.$buefy.toast.open({ type: 'is-dark', message: "Syncing recipe data. This may take some time..." });
      const syncRecipeData = functions.httpsCallable("syncRecipeData");
      syncRecipeData()
        .then(result => {
          this.$buefy.toast.open({ type: 'is-success', message: `${result.data.message}`, duration: 4000 });
        }).catch(error => {
          this.$buefy.toast.open({ type: 'is-danger', message: `${error}`, duration: 4000 });
        });
    },
    async dryRun() {
      const collectionName = this.backfill.collection.trim()
      const propertyName = this.backfill.property.trim()

      this.backfill.dryRunResult = null
      this.backfill.completedCount = null

      try {
        const snapshot = await db.collection(collectionName).get()
        if (!snapshot.docs || snapshot.docs.length === 0) {
          this.$buefy.toast.open({ type: 'is-warning', message: `Collection "${collectionName}" is empty or doesn't exist` })
          return
        }

        const total = snapshot.docs.length
        let affected = total

        if (this.backfill.onlyMissing) {
          affected = snapshot.docs.filter(doc => {
            const data = doc.data()
            return data[propertyName] == null
          }).length
        }

        const displayValue = this.backfill.valueType === 'null' ? 'null' : String(this.parsedBackfillValue)

        this.backfill.dryRunResult = { total, affected, displayValue }
      } catch (error) {
        this.$buefy.toast.open({ type: 'is-danger', message: `Error: ${error.message}` })
      }
    },
    confirmBackfill() {
      const affected = this.backfill.dryRunResult?.affected || 0
      this.$buefy.dialog.confirm({
        title: 'Confirm Backfill',
        message: `This will update <strong>${affected}</strong> documents in <code>${this.backfill.collection}</code>.<br><br>
          Set <code>${this.backfill.property}</code> = <code>${this.backfill.dryRunResult.displayValue}</code><br><br>
          <strong>Are you sure?</strong>`,
        confirmText: 'Run Backfill',
        type: 'is-warning',
        hasIcon: true,
        onConfirm: () => this.runBackfill()
      })
    },
    async runBackfill() {
      const collectionName = this.backfill.collection.trim()
      const propertyName = this.backfill.property.trim()
      const value = this.parsedBackfillValue

      this.backfill.isRunning = true
      this.backfill.progress = 0
      this.backfill.completedCount = null
      this.backfill.rollbackLog = []

      try {
        const snapshot = await db.collection(collectionName).get()
        let docsToUpdate = snapshot.docs

        if (this.backfill.onlyMissing) {
          docsToUpdate = docsToUpdate.filter(doc => {
            const data = doc.data()
            return data[propertyName] == null
          })
        }

        this.backfill.progressMax = docsToUpdate.length

        // Build rollback log before writing
        const rollbackLog = docsToUpdate.map(doc => ({
          docId: doc.id,
          previousValue: doc.data()[propertyName] ?? '__FIELD_MISSING__'
        }))

        // Batch write in chunks of 500
        const BATCH_SIZE = 500
        let updated = 0

        for (let i = 0; i < docsToUpdate.length; i += BATCH_SIZE) {
          const batch = db.batch()
          const chunk = docsToUpdate.slice(i, i + BATCH_SIZE)

          chunk.forEach(doc => {
            const entry = {}
            entry[propertyName] = value
            batch.set(db.collection(collectionName).doc(doc.id), entry, { merge: true })
          })

          await batch.commit()
          updated += chunk.length
          this.backfill.progress = updated
        }

        this.backfill.rollbackLog = rollbackLog
        this.backfill.completedCount = updated
        this.$buefy.toast.open({ type: 'is-success', message: `Backfill complete! Updated ${updated} documents.`, duration: 4000 })
      } catch (error) {
        this.$buefy.toast.open({ type: 'is-danger', message: `Error during backfill: ${error.message}`, duration: 5000 })
      } finally {
        this.backfill.isRunning = false
      }
    },
    confirmRollback() {
      this.$buefy.dialog.confirm({
        title: 'Confirm Rollback',
        message: `This will restore <strong>${this.backfill.rollbackLog.length}</strong> documents in <code>${this.backfill.collection}</code> to their previous values for <code>${this.backfill.property}</code>.<br><br>
          <strong>Are you sure?</strong>`,
        confirmText: 'Rollback',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.runRollback()
      })
    },
    async runRollback() {
      const collectionName = this.backfill.collection.trim()
      const propertyName = this.backfill.property.trim()

      this.backfill.isRunning = true
      this.backfill.progress = 0
      this.backfill.progressMax = this.backfill.rollbackLog.length

      try {
        const BATCH_SIZE = 500
        let restored = 0

        for (let i = 0; i < this.backfill.rollbackLog.length; i += BATCH_SIZE) {
          const batch = db.batch()
          const chunk = this.backfill.rollbackLog.slice(i, i + BATCH_SIZE)

          chunk.forEach(logEntry => {
            const ref = db.collection(collectionName).doc(logEntry.docId)
            if (logEntry.previousValue === '__FIELD_MISSING__') {
              // Field didn't exist before — use FieldValue.delete() to remove it
              const entry = {}
              entry[propertyName] = firebase.firestore.FieldValue.delete()
              batch.update(ref, entry)
            } else {
              const entry = {}
              entry[propertyName] = logEntry.previousValue
              batch.set(ref, entry, { merge: true })
            }
          })

          await batch.commit()
          restored += chunk.length
          this.backfill.progress = restored
        }

        this.backfill.rollbackLog = []
        this.backfill.completedCount = null
        this.backfill.dryRunResult = null
        this.$buefy.toast.open({ type: 'is-success', message: `Rollback complete! Restored ${restored} documents.`, duration: 4000 })
      } catch (error) {
        this.$buefy.toast.open({ type: 'is-danger', message: `Error during rollback: ${error.message}`, duration: 5000 })
      } finally {
        this.backfill.isRunning = false
      }
    },
    createCollectionForUser() {
      const userCollectionName = "users";
      const collectionName = "recipe_collections";
      const userPropertyName = "all_saved_recipe_collection_id";

      db.collection(`${userCollectionName}`).get().then(snapshot => {
        if (snapshot.docs == null) {
          this.$buefy.toast.open({ type: 'is-warning', message: `No ${userCollectionName}` });
          return;
        }

        let users = snapshot.docs.map(user => user.data());
        users = users.filter(user => user.uid != null && user[userPropertyName] == null);

        users.forEach(user => {
          if (user[userPropertyName] == null) {
            const savedCollectionID = db.collection(`${collectionName}`).doc().id;

            db.collection(`${collectionName}`).doc(savedCollectionID).set({
              created_at: Date.now(),
              title: "All saved recipes",
              description: null,
              recipes: {},
              status: "draft",
              position: 1,
              type: "user-all-saved-recipes",
              user_id: user.uid
            });

            const entry = {};
            entry[`${userPropertyName}`] = savedCollectionID;

            db.collection(`${userCollectionName}`).doc(user.uid)
              .set(entry, { merge: true });
          }
        });

        this.$buefy.toast.open({ type: 'is-success', message: `Processed ${users.length} users` });
      });
    }
  }
}
</script>

<style scoped>
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.inline-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
}

.toggle {
  width: 36px;
  height: 20px;
  background: var(--border-medium);
  border-radius: 10px;
  position: relative;
  cursor: pointer;
  transition: background 0.2s ease;
}

.toggle.active {
  background: var(--feeds-green);
}

.toggle::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s ease;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.toggle.active::after {
  transform: translateX(16px);
}

.backfill-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.result-box {
  background: var(--bg-tertiary, #f8fafc);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md, 8px);
  padding: 16px;
}

.result-box.success {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.result-box p {
  margin-bottom: 4px;
}

.result-box p:last-child {
  margin-bottom: 0;
}

.result-box code {
  background: rgba(0,0,0,0.06);
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 13px;
}
</style>
